/**
 * Simple word-level diff using Longest Common Subsequence.
 * Returns arrays of segments tagged as 'equal', 'added', or 'removed'.
 */

export type DiffSegment = {
  text: string;
  type: 'equal' | 'added' | 'removed';
};

function tokenize(str: string): string[] {
  // Split into words and whitespace, preserving both
  return str.match(/\S+|\s+/g) || [];
}

function lcs(a: string[], b: string[]): number[][] {
  const m = a.length;
  const n = b.length;
  const dp: number[][] = Array.from({ length: m + 1 }, () => Array(n + 1).fill(0));
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      if (a[i - 1] === b[j - 1]) {
        dp[i][j] = dp[i - 1][j - 1] + 1;
      } else {
        dp[i][j] = Math.max(dp[i - 1][j], dp[i][j - 1]);
      }
    }
  }
  return dp;
}

export function computeDiff(oldStr: string, newStr: string): { removed: DiffSegment[]; added: DiffSegment[] } {
  const oldTokens = tokenize(oldStr);
  const newTokens = tokenize(newStr);
  const dp = lcs(oldTokens, newTokens);

  // Backtrack to find the diff
  const removedSegments: DiffSegment[] = [];
  const addedSegments: DiffSegment[] = [];

  let i = oldTokens.length;
  let j = newTokens.length;
  const oldResult: DiffSegment[] = [];
  const newResult: DiffSegment[] = [];

  while (i > 0 || j > 0) {
    if (i > 0 && j > 0 && oldTokens[i - 1] === newTokens[j - 1]) {
      oldResult.unshift({ text: oldTokens[i - 1], type: 'equal' });
      newResult.unshift({ text: newTokens[j - 1], type: 'equal' });
      i--;
      j--;
    } else if (j > 0 && (i === 0 || dp[i][j - 1] >= dp[i - 1][j])) {
      newResult.unshift({ text: newTokens[j - 1], type: 'added' });
      j--;
    } else {
      oldResult.unshift({ text: oldTokens[i - 1], type: 'removed' });
      i--;
    }
  }

  // Merge consecutive segments of the same type
  function merge(segments: DiffSegment[]): DiffSegment[] {
    const merged: DiffSegment[] = [];
    for (const seg of segments) {
      const last = merged[merged.length - 1];
      if (last && last.type === seg.type) {
        last.text += seg.text;
      } else {
        merged.push({ ...seg });
      }
    }
    return merged;
  }

  return {
    removed: merge(oldResult),
    added: merge(newResult),
  };
}
